import React, { useState, useRef, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

export interface Option {
    value: string
    label: string
    /** Optional leading visual (e.g. a company favicon) rendered before the label. */
    icon?: React.ReactNode
}

export interface FilterableMultiSelectProps {
    options: Option[]
    value: string[]
    onChange: (value: string[]) => void
    placeholder?: string
    className?: string
    emptyMessage?: string
    disabled?: boolean
}

export const FilterableMultiSelect = ({
    options,
    value,
    onChange,
    placeholder = "Search...",
    className,
    emptyMessage = "No options found",
    disabled = false,
}: FilterableMultiSelectProps) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Handle item selection
    const handleToggle = (optionValue: string) => {
        if (value.includes(optionValue)) {
            onChange(value.filter(v => v !== optionValue))
        } else {
            onChange([...value, optionValue])
        }
        setSearchTerm("")
        setIsOpen(false) // Close dropdown after selection
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    return (
        <div ref={containerRef} className={cn("relative", className)}>
            {/* Search input */}
            <Input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => {
                    if (disabled) return;
                    setSearchTerm(e.target.value)
                    setIsOpen(true)
                }}
                onFocus={() => {
                    if (disabled) return;
                    setIsOpen(true)
                }}
                className="w-full h-[36px]"
                disabled={disabled}
            />

            {/* Dropdown with options */}
            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">
                            {emptyMessage}
                        </div>
                    ) : (
                        <ul className="py-1 bg-white">
                            {filteredOptions.map(option => (
                                <li
                                    key={option.value}
                                    className={cn(
                                        "px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2 bg-white",
                                        value.includes(option.value) && "bg-gray-100"
                                    )}
                                    onClick={() => handleToggle(option.value)}
                                >
                                    {option.icon}
                                    <span className="text-sm font-normal text-vb-text">{option.label}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}

